import React, { useState, useEffect, useRef } from 'react';
import { Card, Tag, Button, Modal, Input, message, Flex } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const MERCADO_PAGO_ACTIVATION_URL = process.env.REACT_APP_MERCADO_PAGO_ACTIVATION_URL;
const DISCONNECT_CONFIRM_COUNTDOWN_SECONDS = 15;

const MpConnectionCardCommunity = ({ providerStatus, onStatusChange, communityTeamId }) => {
  const [prerequisiteModalVisible, setPrerequisiteModalVisible] = useState(false);
  const [stepsModalVisible, setStepsModalVisible] = useState(false);
  const [step, setStep] = useState(1);
  const [pinCode, setPinCode] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [identification, setIdentification] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);
  const [disconnectSummaryLoading, setDisconnectSummaryLoading] = useState(false);
  const [disconnectSummary, setDisconnectSummary] = useState(null);
  const [disconnectCountdown, setDisconnectCountdown] = useState(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
  const [connectLoading, setConnectLoading] = useState(false);
  const disconnectIntervalRef = useRef(null);

  const connected = providerStatus?.connected === true;

  // Trata retorno do OAuth (MP redireciona pra /comunidade/financeiro?mp_connected=1
  // ou ?mp_error=X). Limpa a URL depois e mostra feedback.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mp_connected') === '1') {
      message.success('Conta Mercado Pago vinculada com sucesso!');
      params.delete('mp_connected'); params.delete('team_id');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? '?' + rest : ''));
      onStatusChange?.();
    } else if (params.get('mp_error')) {
      const err = params.get('mp_error');
      const map = {
        invalid_state: 'Sessão expirou. Tenta conectar de novo.',
        token_exchange_failed: 'O Mercado Pago recusou a autorização. Tenta de novo.',
        db_save_failed: 'Deu ruim ao salvar. Tenta de novo.',
        missing_code_or_state: 'Autorização incompleta. Tenta de novo.',
      };
      message.error(map[err] || `Erro ao conectar: ${err}`);
      params.delete('mp_error'); params.delete('team_id');
      const rest = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (rest ? '?' + rest : ''));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectMp = async () => {
    setConnectLoading(true);
    try {
      const r = await api.get('/community/finance/mp-oauth/start', { params: { team_id: communityTeamId } });
      if (r.data.authorize_url) {
        window.location.href = r.data.authorize_url;
      } else {
        message.error('Não deu pra iniciar a conexão. Fala com o suporte.');
        setConnectLoading(false);
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 503) message.error('OAuth do MP não configurado ainda. Fala com o suporte.');
      else message.error(e.response?.data?.message || 'Falhou iniciar a conexão');
      setConnectLoading(false);
    }
  };

  const handleStartActivation = () => {
    setPrerequisiteModalVisible(false);
    setStep(1);
    setPinCode('');
    setStepsModalVisible(true);
  };

  const handleCloseSteps = () => {
    setStepsModalVisible(false);
    setStep(1);
    setPinCode('');
    setIdentification(null);
  };

  const handleConfirmPin = async () => {
    if (!pinCode?.trim()) {
      message.error('Digite o código de 6 números para continuar');
      return;
    }

    try {
      setPinLoading(true);
      const response = await api.post('/community/finance/mp-connection/validate-pin', {
        code: pinCode,
        community_team_id: communityTeamId
      });

      if (response?.data?.activation) {
        setIdentification(response.data.activation);
        setStep(4);
      }
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao validar código de integração');
    } finally {
      setPinLoading(false);
    }
  };

  const handleConfirmIdentification = async () => {
    try {
      setPinLoading(true);
      await api.post('/community/finance/mp-connection/confirm', {
        code: pinCode,
        community_team_id: communityTeamId
      });

      message.success('Integração confirmada com sucesso');
      handleCloseSteps();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao confirmar integração');
    } finally {
      setPinLoading(false);
    }
  };

  const handleCancelIdentification = () => {
    setIdentification(null);
    handleCloseSteps();
  };

  useEffect(() => {
    return () => {
      if (disconnectIntervalRef.current) {
        clearInterval(disconnectIntervalRef.current);
      }
    };
  }, []);

  const closeDisconnectModal = () => {
    if (disconnectIntervalRef.current) {
      clearInterval(disconnectIntervalRef.current);
      disconnectIntervalRef.current = null;
    }
    setDisconnectModalVisible(false);
    setDisconnectSummary(null);
    setDisconnectCountdown(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
  };

  const handleDisconnect = async () => {
    try {
      setDisconnectSummaryLoading(true);
      const response = await api.get('/community/finance/mp-connection/disconnect-summary', {
        params: { team_id: communityTeamId }
      });
      setDisconnectSummary(response?.data || null);
      setDisconnectCountdown(DISCONNECT_CONFIRM_COUNTDOWN_SECONDS);
      setDisconnectModalVisible(true);

      disconnectIntervalRef.current = setInterval(() => {
        setDisconnectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(disconnectIntervalRef.current);
            disconnectIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao buscar resumo de desvinculação');
    } finally {
      setDisconnectSummaryLoading(false);
    }
  };

  const handleConfirmDisconnect = async () => {
    try {
      setDisconnecting(true);
      const response = await api.put('/community/finance/mp-connection/disconnect', {
        community_team_id: communityTeamId
      });
      const { charges_cancelled, purchases_cancelled } = response?.data || {};
      message.success(
        `Conta do Mercado Pago desvinculada com sucesso. Canceladas: ${charges_cancelled ?? 0} cobrança(s) e ${purchases_cancelled ?? 0} compra(s) de crédito pendentes.`
      );
      closeDisconnectModal();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || 'Erro ao desvincular conta do Mercado Pago');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex align="center" justify="space-between" gap="1rem" wrap>
          <Flex align="center" gap="0.6rem">
            <span>Conta Mercado Pago:</span>
            {connected ? (
              <Tag icon={<CheckCircleOutlined />} color="green">Vinculada</Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="red">Não vinculada</Tag>
            )}
          </Flex>

          {connected ? (
            <Button
              danger
              icon={<DisconnectOutlined />}
              loading={disconnectSummaryLoading}
              onClick={handleDisconnect}
            >
              Desvincular Conta
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={connectLoading}
              onClick={() => setPrerequisiteModalVisible(true)}
            >
              Vincular Conta Mercado Pago
            </Button>
          )}
        </Flex>
      </Card>

      <Modal
        title="Vincular sua conta do Mercado Pago"
        open={prerequisiteModalVisible}
        onCancel={() => setPrerequisiteModalVisible(false)}
        footer={[
          <Button key="cancel-oauth" onClick={() => setPrerequisiteModalVisible(false)}>
            Agora não
          </Button>,
          <Button key="start-oauth" type="primary" loading={connectLoading}
            onClick={() => { setPrerequisiteModalVisible(false); handleConnectMp(); }}>
            Conectar no Mercado Pago
          </Button>,
        ]}
      >
        <div style={{ whiteSpace: 'pre-line' }}>
          {`Você vai ser redirecionado pro Mercado Pago pra autorizar o Futezap a usar sua conta pra receber os pagamentos.

            A conta deve estar no seu nome ou de alguém com acesso direto — é ela que vai receber o Pix ou o cartão.

            Importante:

            - Todo valor cobrado cai 100% direto na sua conta MP (menos a taxa nativa do MP).
            - Você pode desvincular a qualquer momento no botão "Desvincular Conta".
            - A responsabilidade pelo que está cobrando é do organizador.`}
        </div>
      </Modal>

      <Modal
        title={`Ativação do Financeiro Comunidade ${step > 1 ? ` - Passo ${step - 1}` : ''}`}
        open={stepsModalVisible}
        onCancel={handleCloseSteps}
        footer={[
          step > 1 && step < 4 && (
            <Button
              key="back-step"
              onClick={() => setStep((prev) => Math.max(1, prev - 1))}
            >
              Voltar
            </Button>
          ),
          step < 3 ? (
            <Button
              key="next-step"
              type="primary"
              onClick={() => setStep((prev) => Math.min(3, prev + 1))}
            >
              Próximo
            </Button>
          ) : step === 3 ? (
            <Button
              key="confirm-step-3"
              type="primary"
              loading={pinLoading}
              onClick={handleConfirmPin}
            >
              Confirmar código
            </Button>
          ) : (
            <>
              <Button
                key="confirm-data"
                type="primary"
                loading={pinLoading}
                onClick={handleConfirmIdentification}
              >
                Confirmar (tudo correto)
              </Button>
              <Button
                key="cancel-data"
                danger
                onClick={handleCancelIdentification}
              >
                Desistir (dados incorretos)
              </Button>
            </>
          )
        ].filter(Boolean)}
      >
        {step === 1 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            {`🔧 Como ativar o Financeiro Comunidade (3 passos simples):

            1️⃣ Criar uma conta no Mercado Pago (caso ainda não tenha)
            2️⃣ Fazer a integração com o Financeiro Comunidade (a gente te guia no processo)
            3️⃣ Digitar o código de ativação gerado depois da ativação no Mercado Pago

            🧾 Em cada cobrança ou compra de crédito, será adicionada uma taxa de transação de R$0,99 + 1% referente a taxa do Mercado Pago, já embutida no valor final e paga pelo jogador.

            O sistema envia até 3 lembretes automáticos pra lembrar sobre o pagamento.

            Pronto! Depois disso, seu Financeiro Comunidade estará ativo e funcionando direto no WhatsApp ⚙️💸`}
          </div>
        )}

        {step === 2 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            <div>{`Depois de abrir a sua conta no Mercado Pago e estar com os dados do acesso em mãos, clique no botão abaixo pra fazer a ativação.`}</div>
            <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 16 }}>
              <Button
                type="primary"
                href={MERCADO_PAGO_ACTIVATION_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Fazer a Ativação no Mercado Pago
              </Button>
            </div>
            <div>{`ATENÇÃO: A gente não solicita seus dados de acesso em nenhum momento, a configuração é sempre feita direto no Mercado Pago.`}</div>
          </div>
        )}

        {step === 3 && (
          <div style={{ whiteSpace: 'pre-line' }}>
            <div>{`🔐 Confirmação de Integração

              Digite o código de 6 números que apareceu na tela depois da autorização com o Mercado Pago.

              Esse código é necessário pra concluir a ativação do Financeiro Comunidade.

              Aguardando sua confirmação. ⏳`}
            </div>
            <Input
              style={{ marginTop: 16 }}
              placeholder="Digite o código de 6 números"
              maxLength={6}
              value={pinCode}
              onChange={(event) => setPinCode(event.target.value)}
            />
          </div>
        )}

        {step === 4 && identification && (
          <div style={{ whiteSpace: 'pre-line' }}>
            {`Identificamos o seu cadastro, confere aí se está tudo certo:

            Nome: ${identification.customer_name}
            Documento: ${identification.customer_document_type} ${identification.document_number}`}
          </div>
        )}
      </Modal>

      <Modal
        title="Desvincular conta do Mercado Pago"
        open={disconnectModalVisible}
        onCancel={closeDisconnectModal}
        footer={[
          <Button key="back-disconnect" onClick={closeDisconnectModal}>
            Voltar
          </Button>,
          <Button
            key="confirm-disconnect"
            danger
            type="primary"
            loading={disconnecting}
            disabled={disconnectCountdown > 0}
            onClick={handleConfirmDisconnect}
          >
            {disconnectCountdown > 0 ? `Aguarde (${disconnectCountdown}s) para confirmar a desvinculação` : 'Sim, desvincular definitivamente'}
          </Button>
        ]}
      >
        <div>
          <p>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            Essa ação é <strong>definitiva e não pode ser desfeita</strong>. Ao desvincular a conta do Mercado Pago, serão <strong>canceladas imediatamente</strong>:
          </p>
          <ul>
            <li><strong>{disconnectSummary?.pending_charges ?? 0} cobrança(s)</strong> pendentes</li>
            <li><strong>{disconnectSummary?.pending_credit_purchases ?? 0} compra(s) de crédito</strong> pendentes</li>
          </ul>
          <p>
            Cobranças e compras já <strong>pagas pelo Mercado Pago</strong> ou com <strong>baixa manual confirmada</strong> não serão alteradas.
          </p>
          <p>
            Depois de desvincular, a geração de novas cobranças e compras de crédito fica bloqueada até vincular uma nova conta. Se reconectar outra conta do Mercado Pago depois, nada do que ficou pendente antes volta a ser reativado.
          </p>
          <p>Tem certeza que quer continuar?</p>
        </div>
      </Modal>
    </>
  );
};

export default MpConnectionCardCommunity;
